import { apiClientV2 } from 'client';
import buildExploreParamsForInvestigationBySearch from 'components/Investigations/buildExploreParamsForInvestigationBySearch';
import queryClient from 'hooks/queryClient';
import { useCallback, useState } from 'react';
import type { RouteParams } from 'types';
import type { Tree } from 'utils/parserTree';
import {
  buildAddEnvelopesErrorOutcome,
  type AddEnvelopesOutcome,
} from './summarizeAddEnvelopesBatch';

type UseAddEnvelopesToInvestigation = () => {
  addByEnvelopeUuid: (
    investigationId: string,
    envelopeUuid: string
  ) => Promise<AddEnvelopesOutcome>;
  addBySearch: (
    investigationId: string,
    navParams: RouteParams,
    tree?: Tree | null
  ) => Promise<AddEnvelopesOutcome>;
  isSubmitting: boolean;
};

const invalidateInvestigationEnvelopesCache = (): void => {
  queryClient.invalidateQueries(['investigations.extras.listEnvelopes']);
};

const useAddEnvelopesToInvestigation: UseAddEnvelopesToInvestigation = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addByEnvelopeUuid = useCallback(
    async (investigationId: string, envelopeUuid: string): Promise<AddEnvelopesOutcome> => {
      setIsSubmitting(true);
      try {
        const response = await apiClientV2.resources.investigations.extras.addEnvelopeByEnvelope({
          urlParams: { investigationId },
          data: { communication_envelope_uuid: envelopeUuid },
        });

        if (response.error != null) {
          return {
            isError: true,
            message: response.error.message || 'Failed to add message',
            refreshEnvelopes: false,
            closeModal: false,
          };
        }

        const inserted = response.data?.inserted ?? 0;
        if (inserted > 0) {
          invalidateInvestigationEnvelopesCache();
          return {
            isError: false,
            message: 'Added message to the investigation',
            refreshEnvelopes: true,
            closeModal: true,
          };
        }

        return {
          isError: false,
          message: 'Message was already in the investigation',
          refreshEnvelopes: false,
          closeModal: true,
        };
      } catch (error) {
        return buildAddEnvelopesErrorOutcome(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  const addBySearch = useCallback(
    async (
      investigationId: string,
      navParams: RouteParams,
      tree?: Tree | null
    ): Promise<AddEnvelopesOutcome> => {
      setIsSubmitting(true);
      try {
        const params = buildExploreParamsForInvestigationBySearch(navParams, tree);
        const response = await apiClientV2.resources.investigations.extras.addEnvelopesBySearch({
          urlParams: { investigationId },
          params,
          data: {},
        });

        if (response.error != null) {
          return {
            isError: true,
            message: response.error.message || 'Failed to add messages',
            refreshEnvelopes: false,
            closeModal: false,
          };
        }

        const body = response.data as { inserted?: number; matched?: number };
        invalidateInvestigationEnvelopesCache();
        return {
          isError: false,
          message: `Added ${body.inserted ?? 0} message(s) (${
            body.matched ?? 0
          } matched current filters)`,
          refreshEnvelopes: true,
          closeModal: true,
        };
      } catch (error) {
        return buildAddEnvelopesErrorOutcome(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  return { addByEnvelopeUuid, addBySearch, isSubmitting };
};

export default useAddEnvelopesToInvestigation;
